import { ApolloError, useLazyQuery } from "@apollo/client";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useEffect, useState } from "react";
import { GET_ASSIGNABLE_BILLABLE_ITEMS } from "../graphql/orderBillableItem";
import { BillableItemSummary } from "../types/billableItemAssignment";
import { fetchAllBillableItemPages } from "../utils/fetchAllBillableItemPages";

/**
 * The billable items assignable to an order: the courier's items, narrowed by the backend
 * to the ones actively linked to the order's end customer — the rule
 * `assignBillableItemToOrder` enforces, so the picker offers only choices that succeed.
 *
 * Nothing is fetched until the end customer is known: an unfiltered query returns the
 * courier's whole catalogue, which is exactly what this picker must not offer.
 */
export function useAssignableBillableItems(
  endCustomerId: string | null,
  isEnabled: boolean
) {
  const { courierId } = useAuth();
  const showError = useErrorToast();
  const [billableItems, setBillableItems] = useState<BillableItemSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchPage] = useLazyQuery(GET_ASSIGNABLE_BILLABLE_ITEMS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!isEnabled || !courierId || !endCustomerId) {
      setBillableItems([]);
      return;
    }
    // A response that arrives after the customer changed (or after unmount) describes the
    // wrong order, and applying it would offer items the mutation rejects. The hook drops
    // it rather than relying on callers to mount it per order.
    let isCurrentRequest = true;
    setIsLoading(true);
    fetchAllBillableItemPages(
      (variables) => fetchPage({ variables }),
      courierId,
      endCustomerId
    )
      .then((loadedBillableItems) => {
        if (!isCurrentRequest) return;
        setBillableItems(loadedBillableItems);
        setIsLoading(false);
      })
      .catch((error) => {
        if (!isCurrentRequest) return;
        setIsLoading(false);
        showError(error as ApolloError);
      });
    return () => {
      isCurrentRequest = false;
    };
  }, [courierId, endCustomerId, isEnabled]);

  return {
    assignableBillableItems: billableItems,
    isLoadingBillableItems: isLoading,
  };
}
